"use client";

import { useMemo, useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { AlertTriangle, Check, ChevronsUpDown, Search } from "lucide-react";
import type { CatalogModel } from "@/lib/llm-models/catalog";
import { perMillion } from "./format";

export function ReasoningBadge() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-violet-50 px-1.5 py-px text-[10px] font-semibold text-violet-700 ring-1 ring-inset ring-violet-200">
      reasoning
    </span>
  );
}

type Props = {
  id: string;
  testId?: string;
  value: string;
  models: CatalogModel[];
  byId: Map<string, CatalogModel>;
  /** The saved model when it is missing from the catalog, offered as an extra first option. */
  unlisted: { value: string; label: string } | null;
  disabled: boolean;
  onChange: (modelId: string) => void;
};

export default function ModelPicker({ id, testId, value, models, byId, unlisted, disabled, onChange }: Props) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => m.name.toLowerCase().includes(q) || m.id.toLowerCase().includes(q));
  }, [models, query]);

  const display = (modelId: string | null) => {
    if (!modelId) return "";
    const m = byId.get(modelId);
    if (m) return m.name;
    return unlisted && unlisted.value === modelId ? unlisted.label : modelId;
  };

  const showUnlisted = unlisted && (!query.trim() || unlisted.label.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div data-testid={testId}>
      <Combobox
        value={value}
        onChange={(next: string | null) => {
          if (next) onChange(next);
        }}
        onClose={() => setQuery("")}
        disabled={disabled}
        immediate
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <ComboboxInput
            id={id}
            className="block h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-10 text-sm font-medium text-slate-900 shadow-sm transition placeholder:font-normal placeholder:text-slate-400 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 data-[disabled]:cursor-not-allowed data-[disabled]:bg-slate-50 data-[disabled]:text-slate-500 data-[disabled]:hover:border-slate-300"
            displayValue={display}
            placeholder="Search models"
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
          />
          <ComboboxButton
            aria-label="Show all models"
            className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-slate-400 hover:text-slate-600 data-[disabled]:cursor-not-allowed"
          >
            <ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
          </ComboboxButton>
        </div>

        <ComboboxOptions
          anchor="bottom start"
          transition
          className="z-50 w-[var(--input-width)] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-900/10 [--anchor-gap:6px] [--anchor-max-height:22rem] focus:outline-none transition duration-100 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
        >
          <div
            aria-hidden="true"
            className="sticky top-0 z-10 -mx-1 -mt-1 mb-1 flex items-center justify-between border-b border-slate-100 bg-white/95 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 backdrop-blur"
          >
            <span>
              {filtered.length} {filtered.length === 1 ? "model" : "models"}
            </span>
            <span>Per 1M tokens, in / out</span>
          </div>

          {showUnlisted ? (
            <ComboboxOption
              value={unlisted.value}
              className="group flex cursor-pointer select-none items-start gap-2 rounded-lg px-3 py-2 data-[focus]:bg-amber-50"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 invisible group-data-[selected]:visible" aria-hidden="true" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{unlisted.label}</p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-700">
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Not in the OpenRouter list
                </p>
              </div>
            </ComboboxOption>
          ) : null}

          {filtered.map((m) => (
            <ComboboxOption
              key={m.id}
              value={m.id}
              className="group flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 data-[focus]:bg-indigo-50"
            >
              <Check className="h-4 w-4 shrink-0 text-indigo-600 invisible group-data-[selected]:visible" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-medium text-slate-900 group-data-[selected]:text-indigo-700">
                    {m.name}
                  </span>
                  {m.supportsReasoning ? <ReasoningBadge /> : null}
                </div>
                <p className="truncate font-mono text-[11px] text-slate-400">{m.id}</p>
              </div>
              <div className="shrink-0 text-right text-xs tabular-nums text-slate-600">
                <span>{perMillion(m.inputPerMillion)}</span>
                <span className="text-slate-300"> / </span>
                <span className="font-medium text-slate-800">{perMillion(m.outputPerMillion)}</span>
              </div>
            </ComboboxOption>
          ))}

          {filtered.length === 0 && !showUnlisted ? (
            <p className="px-3 py-6 text-center text-sm text-slate-500">No models match &ldquo;{query.trim()}&rdquo;.</p>
          ) : null}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
}
